/**
 * What the top-bar search finds without asking the server: admin pages, shortcuts and
 * how-to guides. A guide can carry a tour: steps that open a page and point at a part
 * of it. A step's `target` is a CSS selector, a list of selectors tried in order, or
 * `{ text, tag }` to find a button or tab by its label. `clickOnNext` clicks the target
 * when the counselor presses Next (to open a tab the next step points into).
 */

const SIDEBAR = (path) => `#an-sidebar a[href="${path}"]`;

export const PAGES = [
  { id: 'dashboard', title: 'Dashboard', icon: 'dashboard', to: '/admin/dashboard', keywords: 'home overview briefing needs attention today' },
  { id: 'results', title: 'Results', icon: 'users', to: '/admin/conversation', keywords: 'students records student profile riasec chat conversations' },
  { id: 'review', title: 'Review and release', icon: 'clipboard-check', to: '/admin/assessment-ai', keywords: 'release review approve explanation results waiting withdraw' },
  { id: 'appointments', title: 'Appointments', icon: 'calendar', to: '/admin/appointments', keywords: 'sessions calendar booking schedule walk-in counseling' },
  { id: 'management', title: 'Management', icon: 'settings', to: '/admin/management', keywords: 'users students sections departments import accounts' },
  { id: 'tests', title: 'Test Management', icon: 'clipboard-list', to: '/admin/management?tab=test-management', keywords: 'assign tests deadlines assessments dass sdq ryff riasec' },
  { id: 'programs', title: 'Degree Programs', icon: 'graduation-cap', to: '/admin/management?tab=degree-programs', keywords: 'courses offerings programs college' },
  { id: 'feedback', title: 'Feedback', icon: 'chat', to: '/admin/feedback', keywords: 'ratings comments user feedback' },
  // School admins only: the search hides it for everyone else.
  { id: 'operations', title: 'Operations', icon: 'activity', to: '/admin/operations', keywords: 'term report no-show wait time caseload workload slot use busy hours retake outcomes participation', adminOnly: true },
  { id: 'audit-log', title: 'Audit log', icon: 'history', to: '/admin/audit-log', keywords: 'audit log history who changed what edited deleted activity before after trail record changes downloads sign-ins', adminOnly: true },
  { id: 'profile', title: 'My profile', icon: 'user-circle', to: '/admin/profile', keywords: 'account photo password mobile' },
];

export const ACTIONS = [
  { id: 'to-review', title: 'Results waiting for review', icon: 'clipboard-check', to: '/admin/assessment-ai?status=to_review', keywords: 'pending unreleased waiting release' },
  { id: 'needs', title: 'Sessions that need you', icon: 'calendar-event', to: '/admin/appointments?view=needs', keywords: 'notes reschedule requests complete' },
  { id: 'upcoming', title: 'Upcoming sessions', icon: 'calendar-check', to: '/admin/appointments?view=upcoming', keywords: 'next appointments schedule' },
  { id: 'hours', title: 'Set my open hours', icon: 'clock', to: '/admin/appointments?view=schedule', keywords: 'availability weekly hours slots booking time off reminders' },
  { id: 'deadlines', title: 'Test deadlines', icon: 'hourglass', to: '/admin/management?tab=test-management', keywords: 'deadline extend due date' },
];

export const GUIDES = [
  {
    id: 'dashboard-tour',
    area: 'Getting started',
    title: 'Tour the dashboard',
    summary: 'What each part of the Overview tells you, top to bottom.',
    keywords: 'dashboard overview start new first time briefing tiles needs attention',
    path: '/admin/dashboard',
    steps: [
      'The briefing sums up what changed since you last opened ANNA.',
      'The tiles count results to review, students without a session and today’s sessions.',
      'Needs attention puts High and Critical results first. Open one to review it, or schedule a follow-up.',
      'How students are doing shows each test’s bands across your sections.',
    ],
    tour: [
      { path: '/admin/dashboard', target: '[data-tour="global-search"]', title: 'Search or ask from anywhere', body: 'Find a student, a result or a page. Type a question and ANNA answers from your school’s records. Press ⌘K or / to open it.' },
      { path: '/admin/dashboard', target: ['.gh-brief', '[aria-labelledby="gh-brief-title"]'], title: 'Your briefing', body: 'What changed since you last opened ANNA, written fresh each visit.' },
      { path: '/admin/dashboard', target: '.gh-tiles', title: 'The counts that matter', body: 'Results to review, students with no session yet and today’s sessions. Each tile opens its list.' },
      { path: '/admin/dashboard', target: '#gh-attention', title: 'Needs attention', body: 'Critical and High results come first. Open one to review and release it, or schedule a follow-up.' },
      { path: '/admin/dashboard', target: '.gh-section-head', title: 'How students are doing', body: 'Bands for every test across your sections. Use the filters to focus on one level or school year.' },
    ],
  },
  {
    id: 'release-result',
    area: 'Results',
    title: 'Review and release a result',
    summary: 'Read a finished test, check ANNA’s explanation and let the student see it.',
    keywords: 'release result review approve explanation student see publish withdraw',
    path: '/admin/assessment-ai?status=to_review',
    steps: [
      'Open Review and release from the sidebar. Each test tab shows how many results are waiting.',
      'Pick a test, keep the To review view, then click a result to open it.',
      'Read the scores and ANNA’s draft explanation. Edit the explanation if you need to.',
      'Press Release result. The student sees it on their Results page.',
    ],
    tour: [
      { target: SIDEBAR('/admin/assessment-ai'), wait: 600, title: 'Review and release', body: 'Every finished test that waits for a counselor is here. The dot shows when something is waiting.' },
      { path: '/admin/assessment-ai', target: '.ar-tabs', title: 'Pick a test', body: 'One tab per assessment. The number is how many results are waiting for review.' },
      { path: '/admin/assessment-ai', target: '.ar-status-tabs', title: 'Choose a view', body: 'To review is what still needs you. Released is what students can already see.' },
      { path: '/admin/assessment-ai', target: ['.ar-table tbody tr', '.ar-panel'], title: 'Open a result', body: 'Click a row to open the scores and ANNA’s draft explanation. Release result is at the bottom of that panel.' },
      { path: '/admin/assessment-ai', target: '.ar-rules', title: 'Release rules', body: 'These decide whether results wait for you or show right away. Change rules opens them.' },
    ],
  },
  {
    id: 'find-student',
    area: 'Results',
    title: 'Find a student’s results',
    summary: 'Every test a student took, from one place.',
    keywords: 'find student search profile history results riasec record',
    path: '/admin/conversation',
    steps: [
      'Type the student’s name or ID number in the search bar at the top.',
      'Pick the student to open their profile with every test they took.',
      'Pick a result in the list to open it in Review and release.',
    ],
    tour: [
      { target: '[data-tour="global-search"]', title: 'Start with search', body: 'Type a name or ID number. Students, their results and their sessions show up as you type.' },
      { target: SIDEBAR('/admin/conversation'), wait: 600, title: 'Or browse Results', body: 'Results lists every student with a finished test, with filters for section and test.' },
    ],
  },
  {
    id: 'book-session',
    area: 'Sessions',
    title: 'Book a session for a student',
    summary: 'Set up a counseling session on your calendar.',
    keywords: 'book schedule session appointment follow-up counseling student meet',
    path: '/admin/appointments',
    steps: [
      'Open Appointments from the sidebar.',
      'Press Book a session.',
      'Pick the student, the reason and one of your open slots, then book it. The student gets the booking and a reminder.',
    ],
    tour: [
      { target: SIDEBAR('/admin/appointments'), wait: 600, title: 'Appointments', body: 'All your sessions: what needs you, what is coming up and what is done.' },
      { path: '/admin/appointments', target: { text: 'Book a session', tag: 'button' }, title: 'Book a session', body: 'Pick the student, the reason and a slot from your open hours.' },
      { path: '/admin/appointments', target: ['.aph-views', '.aph'], title: 'Your lists', body: 'Under Sessions, each tab is a list with its count. Needs you shows notes owed and reschedule requests; the line under the tabs says what is in the open one. Your schedule, next to Sessions, holds your weekly hours, time off and reminders.' },
      { path: '/admin/appointments', target: ['.ap-list', '.ap-layout'], title: 'Your sessions', body: 'Click a session for details, notes and actions like marking it complete.' },
    ],
  },
  {
    id: 'walk-in',
    area: 'Sessions',
    title: 'Log a walk-in session',
    summary: 'Record a student who came in without booking.',
    keywords: 'walk-in walk in unscheduled visit log record session drop in',
    path: '/admin/appointments',
    steps: [
      'Open Appointments.',
      'Press Log walk-in.',
      'Pick the student and the reason, then add your notes.',
    ],
    tour: [
      { path: '/admin/appointments', target: { text: 'Log walk-in', tag: 'button' }, title: 'Log walk-in', body: 'Record a session that happened without a booking. It counts like any other session.' },
      { path: '/admin/appointments', target: ['.ap-list', '.ap-layout'], title: 'It shows up here', body: 'Walk-ins appear with your other sessions, ready for notes.' },
    ],
  },
  {
    id: 'reschedule',
    area: 'Sessions',
    title: 'Answer a reschedule request',
    summary: 'Approve a new time or decline when a student asks to move a session.',
    keywords: 'reschedule request move session approve decline new time change',
    path: '/admin/appointments?view=needs',
    steps: [
      'Requests show in Needs attention on the dashboard, under Requests.',
      'They also show in Appointments, under Needs you, marked Wants a new time.',
      'Open the request, then approve it with a new time or decline it.',
    ],
    tour: [
      { path: '/admin/dashboard', target: '#gh-attention', title: 'On the dashboard', body: 'Reschedule requests are listed in Needs attention, under Requests.' },
      { path: '/admin/appointments?view=needs', target: ['.aph-tabs [data-view="needs"]', '.aph-views'], title: 'In Appointments', body: 'Needs you holds requests and sessions still waiting for notes. Its count turns red while something is waiting.' },
      { path: '/admin/appointments?view=needs', target: ['.ap-list', '.ap-layout'], title: 'Open the request', body: 'Pick a session marked Wants a new time, then approve a new time or decline.' },
    ],
  },
  {
    id: 'open-hours',
    area: 'Setup',
    title: 'Set your open hours',
    summary: 'Choose when students can book you.',
    keywords: 'open hours availability weekly slots schedule time off calendar booking',
    path: '/admin/appointments?view=schedule',
    steps: [
      'Open Appointments and pick Your schedule.',
      'Drag across the weekly calendar to add open hours. Click a block to remove it.',
      'Save. Students can book only these hours.',
    ],
    tour: [
      { path: '/admin/appointments?view=schedule', target: ['.pf-cal', '.ap-hours', '.ar-page'], title: 'Your week', body: 'Drag across the calendar to open hours. Students can book only inside them.' },
      { path: '/admin/appointments?view=schedule', target: ['.ap-hours-side', '.ap-hours'], title: 'Taking a day off?', body: 'Time off blocks your hours for specific days without changing your weekly pattern. Session reminders are here too.' },
    ],
  },
  {
    id: 'assign-test',
    area: 'Setup',
    title: 'Assign a test to sections',
    summary: 'Choose who takes DASS-42, SDQ, Ryff or RIASEC.',
    keywords: 'assign test assessment sections levels students take dass sdq ryff riasec',
    path: '/admin/management?tab=test-management',
    steps: [
      'Open Management, then Test Management.',
      'Find the test and press Assign.',
      'Pick the departments, levels or sections, check the preview and save.',
    ],
    tour: [
      { path: '/admin/management', target: { text: 'Test Management', tag: '[role="tab"], button' }, clickOnNext: true, title: 'Test Management', body: 'Tests, who takes them and their deadlines live here. Next opens it for you.' },
      { path: '/admin/management', target: { text: 'Assign', tag: 'button' }, title: 'Assign', body: 'Pick the sections or levels who should take this test. You see who is included before saving.' },
      { path: '/admin/management', target: { text: 'Set deadline', tag: 'button' }, title: 'Deadlines', body: 'Set the date each group should finish by. You can extend it later.' },
    ],
  },
  {
    id: 'add-users',
    area: 'Setup',
    title: 'Add or import students',
    summary: 'One at a time or from a CSV or Excel file.',
    keywords: 'add user import csv excel students accounts create upload enroll',
    path: '/admin/management',
    steps: [
      'Open Management. User Management is the first tab.',
      'Press Add user for one person, or Import users for a file.',
      'Imports go in four steps: file, placement, review, import. The section you pick in Placement applies to the whole file.',
      'Start from Download the template. In Review, fix rows marked Need a fix and remove ones already in ANNA.',
      'Activation emails go out as accounts are created.',
    ],
    tour: [
      { path: '/admin/management', target: { text: 'Import users', tag: 'button' }, title: 'Import users', body: 'Upload a CSV or Excel file. ANNA checks every row before anything is saved.' },
      { path: '/admin/management', target: { text: 'Add user', tag: 'button' }, title: 'Add user', body: 'Add one person with their section.' },
    ],
  },
  {
    id: 'ask-anna',
    area: 'Getting started',
    title: 'Ask ANNA about your students',
    summary: 'Questions answered from your school’s records.',
    keywords: 'ask anna ai assistant question chat privacy how does',
    path: '/admin/dashboard',
    steps: [
      'Type a question in the search bar, like “Who has a High result but no session yet?”. Questions switch the bar to Ask ANNA.',
      'Press Enter to ask, or Tab to ask anything you typed.',
      'Names you type are replaced before the question leaves ANNA. The AI sees coded students; names appear only on your screen.',
      'Each answer shows whether its numbers match ANNA’s records and what ANNA looked up.',
    ],
    tour: [
      { target: '[data-tour="global-search"]', title: 'Type a question here', body: 'When it reads like a question, the bar turns into Ask ANNA. Press Enter to ask.' },
    ],
  },
  {
    id: 'bands',
    area: 'Results',
    title: 'What the test results mean',
    summary: 'DASS-42, SDQ and Ryff bands, and RIASEC types, in plain words.',
    keywords: 'bands meaning severe extremely normal mild moderate high low close to average dass sdq ryff riasec interpret score types realistic investigative artistic social enterprising conventional',
    path: '/admin/dashboard',
    steps: [
      'DASS-42 scores depression, anxiety and stress separately: Normal, Mild, Moderate, Severe or Extremely severe.',
      'SDQ bands go Close to average, Slightly raised, High and Very high. For prosocial behavior, low is the concern.',
      'Ryff shows well-being as High, Moderate or Low. Low is the concern.',
      'RIASEC shows interests, not concerns: Realistic, Investigative, Artistic, Social, Enterprising and Conventional. ANNA matches them to programs.',
      'These are screening bands, not diagnoses. Use them to decide who to talk to first.',
    ],
  },
  {
    id: 'export-report',
    area: 'Results',
    title: 'Export a report',
    summary: 'PDF, Excel, CSV or chart images that follow your filters.',
    keywords: 'export download report pdf excel csv png print summary data generate',
    path: '/admin/dashboard',
    steps: [
      'Open the Dashboard and pick a tab: Overview, RIASEC, DASS-42, Ryff Scale or SDQ.',
      'Set the filters. Every export follows them.',
      'Press Export. Overview has the Guidance Report (PDF) and the Student Summary (Excel); each test tab has its charts and a CSV.',
      'Feedback and All users have their own export buttons too.',
    ],
    tour: [
      { path: '/admin/dashboard', target: '#dropdown-export-button', title: 'Export', body: 'The menu changes with the tab you are on and uses your current filters.' },
    ],
  },
  {
    id: 'feedback',
    area: 'Results',
    title: 'Read user feedback',
    summary: 'Ratings and comments on each test and the landing page.',
    keywords: 'feedback ratings comments reviews stars export csv',
    path: '/admin/feedback',
    steps: [
      'Open Feedback from the sidebar.',
      'Pick a test or Landing page, and who: everyone, signed in or guests.',
      'Filter by rating, comments or school year. Export CSV downloads what you see.',
    ],
  },
  {
    id: 'school-year',
    area: 'Setup',
    title: 'Set up the school year',
    summary: 'The dates every report, filter and deadline counts from.',
    keywords: 'school year dates start end reports active upcoming default june may setup new year calendar',
    path: '/admin/management',
    steps: [
      'Open Management and press School year settings.',
      'Set when the year starts and ends, or press Use default for June 1 to May 31. Check the name shown in reports.',
      'Press Save school year. Results, reports and filters now count from these dates.',
      'The next year can be set up once the current one ends. Past years stay under Previous school years.',
    ],
    tour: [
      { path: '/admin/management', target: { text: 'School year settings', tag: 'button' }, title: 'School year settings', body: 'Set the dates your reports, filters and deadlines count from.' },
    ],
  },
  {
    id: 'test-deadlines',
    area: 'Setup',
    title: 'Set or extend test deadlines',
    summary: 'One date per test, and more time for the people who need it.',
    keywords: 'deadline due date extend test assessment late more time',
    path: '/admin/management?tab=test-management',
    steps: [
      'Open Management, then Test Management.',
      'Press Set deadlines and pick a date for each test. Leave a test empty for no deadline.',
      'Press Save deadlines.',
      'To give a few people more time, open All users, select them and press Extend deadline. It covers every test they haven’t finished.',
    ],
    tour: [
      { path: '/admin/management?tab=test-management', target: { text: 'Set deadlines', tag: 'button' }, title: 'Set deadlines', body: 'One date per test. Leave a test empty if it has no deadline.' },
      { path: '/admin/management?tab=student-management', target: '.mg-short.is-users', title: 'More time for a few people', body: 'Open All users, select them and press Extend deadline.' },
    ],
  },
  {
    id: 'sections',
    area: 'Setup',
    title: 'Organize departments and sections',
    summary: 'Levels and sections decide who takes which test.',
    keywords: 'sections departments grade level college add section create class organize strand program',
    path: '/admin/management',
    steps: [
      'Open Management and click a department card.',
      'Add a level. The button follows the department, like Add grade level or Add college.',
      'Open the level and press Add sections.',
      'Each section’s menu has View users and Edit section. Only empty sections can be deleted.',
    ],
    tour: [
      { path: '/admin/management?tab=student-management', target: '.mg-dept-area', title: 'Departments', body: 'Click a department to see its levels and sections, and add new ones.' },
    ],
  },
  {
    id: 'degree-programs',
    area: 'Setup',
    title: 'Add degree programs',
    summary: 'The programs ANNA recommends to students.',
    keywords: 'degree programs courses offerings college add program tuition recommendation catalog',
    path: '/admin/management?tab=degree-programs',
    steps: [
      'Open Management, then Degree Programs.',
      'Press Add program, then search or filter the catalog by strand or RIASEC area.',
      'Select programs, press Add and confirm.',
      'Edit sets tuition, length and links. More, then Remove, takes a program off your list.',
    ],
    tour: [
      { path: '/admin/management?tab=degree-programs', target: { text: 'Add program', tag: 'button' }, title: 'Add program', body: 'Pick from the catalog. Students see these when ANNA recommends programs.' },
    ],
  },
  {
    id: 'resend-activation',
    area: 'Accounts',
    title: 'Resend an activation email',
    summary: 'For people who never activated their account.',
    keywords: 'activation email resend inactive account not activated invite welcome',
    path: '/admin/management',
    steps: [
      'Open Management, press All users, then the Inactive tab.',
      'Press Resend next to a person, then Send email. Or select several and press Resend activation.',
      'More, then Resend to all inactive, emails everyone who hasn’t activated yet.',
    ],
    tour: [
      { path: '/admin/management?tab=student-management', target: '.mg-short.is-users', title: 'All users', body: 'Everyone in your school. The Inactive tab lists accounts that haven’t been activated.' },
    ],
  },
  {
    id: 'manage-account',
    area: 'Accounts',
    title: 'Reset a password or deactivate an account',
    summary: 'Passwords, access and profile details for one person.',
    keywords: 'password reset set password deactivate activate delete user account edit profile locked out',
    path: '/admin/management',
    steps: [
      'Open Management, press All users and search for the person.',
      'Open the row’s More menu for Set password, Deactivate or Delete user. Passwords need at least 8 characters.',
      'Click the person, then Edit Profile, to change their details.',
    ],
    tour: [
      { path: '/admin/management?tab=student-management', target: '.mg-short.is-users', title: 'All users', body: 'Search for the person, then use the More menu on their row.' },
    ],
  },
  {
    id: 'more-attempts',
    area: 'Accounts',
    title: 'Give someone more attempts',
    summary: 'When a student needs to retake a test.',
    keywords: 'attempts retake again more tries limit reset test chatbot',
    path: '/admin/management',
    steps: [
      'Open Management, press All users and select the people.',
      'Press Add attempts, choose how many for each test, then confirm.',
      'For one person, open their section and use Change attempts.',
    ],
    tour: [
      { path: '/admin/management?tab=student-management', target: '.mg-short.is-users', title: 'All users', body: 'Select the people, then press Add attempts.' },
    ],
  },
  {
    id: 'sign-in-help',
    area: 'Troubleshooting',
    title: 'Can’t sign in',
    summary: 'Forgotten passwords and accounts without admin access.',
    keywords: 'login log in sign in password forgot locked access admin cannot reset',
    path: '/admin/dashboard',
    steps: [
      'Check that you are on your school’s ANNA address and using your staff email.',
      'On the sign-in page, use Forgot password?, then Send reset link.',
      '“This account doesn’t have admin access” means it isn’t a staff account. Ask a school admin to check it.',
    ],
  },
  {
    id: 'missing-data',
    area: 'Troubleshooting',
    title: 'A student or result is missing',
    summary: 'Filters, school years and results waiting for review.',
    keywords: 'missing not showing data wrong filter school year result not visible cannot see empty',
    path: '/admin/conversation',
    steps: [
      'Clear the filters and check the school year. Results outside its dates are hidden.',
      'RIASEC keeps only each student’s latest result.',
      'A result students can’t see yet is waiting in Review and release.',
    ],
  },
  {
    id: 'operations-tour',
    area: 'Running the office',
    title: 'Read the Operations page',
    summary: 'Sessions, waiting times and each counselor’s load, with a term report to download.',
    keywords: 'operations term report no-show wait caseload workload busy hours slot use principal',
    path: '/admin/operations',
    adminOnly: true,
    steps: [
      'Pick the period: a school year or your own dates. Every number on the page follows it.',
      'The four tiles are the headline: who took a test, days to get a session, no-shows and retakes that came out better.',
      'Sessions and Waiting show what happened and how long students waited. Busy hours shows when they come.',
      'Counselors compares open hours, sessions, no-shows and slot use.',
      'Download term report gives the same numbers as a PDF for the principal or a program review.',
    ],
    tour: [
      { path: '/admin/operations', target: '[data-tour="op-filters"]', title: 'Pick the period', body: 'A school year, all time or your own dates. Every number on the page follows it.' },
      { path: '/admin/operations', target: '.op-tiles', title: 'The headline', body: 'Who took a test, how long a session takes to get, the no-show rate and retakes that came out better. Each tile opens its detail.' },
      { path: '/admin/operations', target: '#op-sessions', title: 'Sessions', body: 'Held, missed, cancelled and ahead, by week or month.' },
      { path: '/admin/operations', target: '#op-waiting', title: 'Waiting', body: 'Days from booking to session, and from a High or Critical result to the first session. Open requests sit here too.' },
      { path: '/admin/operations', target: '#op-busy', title: 'Busy hours', body: 'When sessions happen. Open hours can follow where students actually come.' },
      { path: '/admin/operations', target: '#op-counselors', title: 'Counselors', body: 'Open hours, sessions, no-shows, the week ahead and how full the last four weeks were.' },
      { path: '/admin/operations', target: '[data-tour="op-report"]', title: 'Term report', body: 'The same numbers as a PDF, ready for the principal or a guidance program review.' },
    ],
  },
  {
    id: 'audit-log-tour',
    area: 'Running the office',
    title: 'Find out who changed something',
    summary: 'Every change in ANNA as a sentence, with what it was before and after.',
    keywords: 'audit log who changed history edited deleted before after record trail',
    path: '/admin/audit-log',
    adminOnly: true,
    steps: [
      'Pick the period, then type a name, a section or a word like “deadline” in the search.',
      'The area chips and the people menu narrow the list; “Only important” keeps role changes, deletions, turned-off accounts and downloads of results.',
      'Open an entry to see each field before and after. Session notes and passwords only say they changed.',
      'History shows everything that ever happened to that session, person or setting.',
      'Download gives the same list as a spreadsheet.',
    ],
    tour: [
      { path: '/admin/audit-log', target: '[data-tour="al-period"]', title: 'Pick the period', body: 'Today, the last week, month or 90 days, or all time.' },
      { path: '/admin/audit-log', target: '[data-tour="al-filters"]', title: 'Narrow it down', body: 'Search names and values, pick an area or a person, or keep only the important changes.' },
      { path: '/admin/audit-log', target: '.al-entry', title: 'Before and after', body: 'Open an entry to see what changed. History shows the whole trail of that record.' },
    ],
  },
];

export const GUIDE_BY_ID = Object.fromEntries(GUIDES.map((guide) => [guide.id, guide]));
export const FEATURED_GUIDES = ['dashboard-tour', 'release-result', 'book-session'];
